"use client";

import styles from "./RootLayout.module.scss";
import { FC, useEffect, useRef, useState } from "react";
import { classNames } from "@/lib/utils";
import Header from "@/components/header/Header";
import { Stock } from "@/models/stock";
import { useRecoilState, useRecoilValue } from "recoil";
import { useRouter } from "next/router";
import { Box, Button, Card, Container, Drawer, IconButton, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { hideTickertapeState, userState, textColorState, themeState, lighterBackgroundColorState, darkerBackgroundColorState, stockReportAvailabilityState, tradingIdeaAvailabilityState } from "@/lib/store";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "@/components/error-fallback/ErrorFallback";
import ContentLayout from "../content/ContentLayout";
import StockHeaderInfo from "@/components/stock-header-info/StockHeaderInfo";
import StockLinks from "@/components/stock-links/StockLinks";
import StockReportBadge from "@/components/stock-report-badge/StockReportBadge";
import NextCorporateActionChip from "@/components/next-corporate-action-chip/NextCorporateActionChip";
import SettingsIcon from "@mui/icons-material/Settings";
import TickertapeSettings from "@/components/tickertape-settings/TickertapeSettings";
import NewTickerTape from "@/components/new-ticker-tape/NewTickerTape";
import StockHeaderLogo from "@/components/stock-header-logo/StockHeaderLogo";
import { useSetRecoilState } from "recoil";
import { getStockReportExists } from "@/controllers/market-intelligence";
import { getTradingIdeaExists } from "@/controllers/trading-ideas";
import TradingIdeaBadge from "@/components/trading-idea-badge/TradingIdeaBadge";
import { useIdleEffect } from "@/lib/defer";
import AboutThisData from "@/components/locked-value-panel/AboutThisData";
import LockedValuePanel from "@/components/locked-value-panel/LockedValuePanel";
import StockKeyData from "@/components/stock-key-data/StockKeyData";
import SiteFooter from "@/components/site-footer/SiteFooter";
import { StockShortKeyData } from "@/controllers/stock";

interface PropsTypes {
    stock?: Stock;
    // Stock overview and short-interest tab only (their getServerSideProps); see StockKeyData.
    shortData?: StockShortKeyData | null;
}

const RootLayout: FC<PropsTypes> = ({ children, stock, shortData }) => {
    const mainRef = useRef(null);
    const isMobile = useMediaQuery("(max-width: 768px)");

    const [user] = useRecoilState(userState);
    const textColor = useRecoilValue(textColorState);
    const theme = useRecoilValue(themeState);
    const lighterBackgroundColor = useRecoilValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useRecoilValue(darkerBackgroundColorState);

    const { pathname, push } = useRouter();

    const [page, setPage] = useState("");
    const [scrolledDown, setScrolledDown] = useState(false);
    const [settingsModal, setSettingsModal] = useState(false);
    const [hideTickertape, setHideTickertape] = useRecoilState(hideTickertapeState);

    const anchorPosition = isMobile ? "bottom" : "right";
    const id = settingsModal ? "simple-popover" : undefined;

    const handleClose = () => {
        setSettingsModal(false);
    };

    // ── Stock Report availability ────────────────────────────────
    // Single shared fetch when a stock-page mounts (or the user
    // navigates between tickers). Result is published into the
    // ``stockReportAvailabilityState`` atom so both the StockLinks
    // tab strip (which inserts a "Stock Report" tab when one
    // exists) and the top-bar ``<StockReportBadge/>`` (which adds
    // the click-through pill next to the company name on mobile
    // and next to the market-hours indicator on desktop) read
    // from the same source. ``null`` while resolving so consumers
    // can distinguish "still loading" from "no report exists".
    //
    // The *probe* is deferred to the browser's first idle period
    // (``useIdleEffect``): at about a second it was one of the two slowest
    // requests on a cold stock page, and nothing the reader is looking at
    // waits on it - it adds a tab and a pill. Starting it after the page has
    // drawn keeps it out of the mount burst the chart's own data is in.
    //
    // Clearing the previous ticker's answer is NOT deferred. It has to happen
    // in the same commit as the navigation, or the old stock's tab and badge
    // stay on screen against the new stock's name for as long as the deferral
    // lasts.
    const setStockReportAvailability = useSetRecoilState(stockReportAvailabilityState);
    useEffect(() => {
        setStockReportAvailability(null);
    }, [stock?.ticker, stock?.exchangesymbol, setStockReportAvailability]);
    //
    // No AbortController, on purpose. `installGetDeduplication` refuses to
    // share any GET carrying a signal - joining them would let one caller's
    // abort cancel another's request - so passing one opted these probes out of
    // the very machinery that exists to collapse duplicate calls, and the page
    // issued this request twice. A plain `cancelled` flag ignores the late
    // answer just as effectively, and lets a second mount join the first
    // request instead of starting another. The response is a few bytes; not
    // aborting it costs nothing worth the duplicate.
    useIdleEffect(() => {
        if (!stock?.ticker || !stock?.exchangesymbol) return;
        const ticker = stock.ticker;
        const exchange = stock.exchangesymbol;
        let cancelled = false;
        getStockReportExists(ticker, undefined, exchange)
            .then((r) => {
                if (cancelled) return;
                setStockReportAvailability({
                    exists: !!r?.exists,
                    headline: r?.headline ?? null,
                    generatedAt: r?.generatedAt ?? null,
                    deepLinkPath: `/s/${exchange}/${ticker}/stock-report`,
                });
            })
            .catch(() => {
                // 404 / network errors collapse to "no report" — the
                // badge / tab simply don't appear. Never surface a
                // user-visible failure for the existence probe; the
                // rest of the stock page is unaffected.
                if (!cancelled) setStockReportAvailability({ exists: false });
            });
        return () => {
            cancelled = true;
        };
    }, [stock?.ticker, stock?.exchangesymbol, setStockReportAvailability]);

    // ── Trading Idea availability ────────────────────────────────
    // Same shape as above. The probe is gated server-side, so a user
    // without the product group gets exists=false and the tab and
    // callout simply never render — there is no client-side check to
    // forget, and no state in which an empty tab could appear.
    // Deferred, and cleared eagerly, for the same reasons as the report probe.
    const setTradingIdeaAvailability = useSetRecoilState(tradingIdeaAvailabilityState);
    useEffect(() => {
        setTradingIdeaAvailability(null);
    }, [stock?.ticker, stock?.exchangesymbol, setTradingIdeaAvailability]);
    // Same dedupe reasoning as the report probe above.
    useIdleEffect(() => {
        if (!stock?.ticker || !stock?.exchangesymbol) return;
        const ticker = stock.ticker;
        const exchange = stock.exchangesymbol;
        let cancelled = false;
        getTradingIdeaExists(ticker, undefined, exchange)
            .then((r) => {
                if (cancelled) return;
                setTradingIdeaAvailability({
                    exists: !!r?.exists,
                    id: r?.id,
                    headline: r?.headline ?? null,
                    direction: r?.direction,
                    publishedAt: r?.publishedAt ?? null,
                    deepLinkPath: `/s/${exchange}/${ticker}/trading-idea`,
                });
            })
            .catch(() => {
                // Fail closed, exactly like the report probe: any error
                // means no tab. Better to hide a real idea than to show
                // a tab we cannot stand behind.
                if (!cancelled) setTradingIdeaAvailability({ exists: false });
            });
        return () => {
            cancelled = true;
        };
    }, [stock?.ticker, stock?.exchangesymbol, setTradingIdeaAvailability]);

    useEffect(() => {
        if (pathname.includes("corporate-actions")) setPage("corporate-actions");
        else if (pathname.includes("commodity-exposure")) setPage("commodity-exposure");
        else if (pathname.includes("trading-signals")) setPage("trading-signals");
        else if (pathname.includes("short-interest")) setPage("short-interest");
        else if (pathname.includes("stock-scores")) setPage("stock-scores");
        else if (pathname.includes("fundamentals")) setPage("fundamentals");
        else if (pathname.includes("analysts")) setPage("analysts");
        else if (pathname.includes("insiders")) setPage("insiders");
        else if (pathname.includes("dividend")) setPage("dividend");
        else if (pathname.includes("holders")) setPage("holders");
        else if (pathname.includes("options")) setPage("options");
        else if (pathname.includes("events")) setPage("events");
        else if (pathname.includes("peers")) setPage("peers");
        else if (pathname.includes("eps")) setPage("eps");
        else if (pathname.includes("stocks-stamps")) setPage("stock-stamps");
        else if (pathname.endsWith("/bonds")) setPage("bonds");
        else if (pathname.endsWith("/stock-report")) setPage("stock-report");
        else setPage("overview");
    }, [pathname]);

    useEffect(() => {
        if (!user?.email) return;

        const tickertapeVisibility = user?.settings?.module_settings["tickerTape->tickertape-visibility"];

        if (tickertapeVisibility) {
            setHideTickertape(tickertapeVisibility.isHidden);
        } else {
            setHideTickertape(false);
        }
    }, [user]);

    const logError = (error: Error, info: { componentStack: string }) => {
        console.error(error, info);
    };

    const handleScroll = () => {
        if (mainRef.current) setScrolledDown(mainRef.current.scrollTop > 0 ? true : false);
    };
    useEffect(() => {
        if (mainRef.current) mainRef.current.addEventListener("scroll", handleScroll);

        return () => {
            if (mainRef.current) mainRef.current.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const goToSettings = () => {
        push("/account");
        setSettingsModal(false);
    };

    return (
        <div className="ion-app">
            <div className={styles.RootLayout} ref={mainRef}>
                <Box sx={{ display: "flex", width: "100%", position: "fixed", justifyContent: "flex-end" }}>
                    {!hideTickertape && <NewTickerTape />}
                    <Box className={styles.tickertapeSettingsBtn} sx={{ backgroundColor: theme === "dark" ? darkerBackgroundColor : lighterBackgroundColor }}>
                        <Tooltip title="Tickertape Settings" arrow>
                            <IconButton className={styles.button} onClick={() => setSettingsModal(true)} sx={{ p: 0 }}>
                                <SettingsIcon style={{ color: textColor, fontSize: "18px" }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                <Header ticker={stock?.ticker} exchange={stock?.exchangesymbol} stock={stock} scrolledDown={scrolledDown} />

                <main className={classNames(styles[user.email && "user"], stock && styles.mainStockPage, stock && page === "stock-report" && styles.flowsWithPage)}>
                    {
                        // @ts-ignore
                        <ErrorBoundary fallbackRender={ErrorFallback} onError={logError}>
                            {stock ? (
                                <ContentLayout
                                    title={
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexWrap: "wrap" }}>
                                                {<StockHeaderLogo theme={theme} stock={stock} />}
                                                <Box sx={{ ml: 1 }}>
                                                    <Typography variant="h1" sx={{ fontWeight: "bold", letterSpacing: "0.5px" }}>
                                                        {stock.name}
                                                    </Typography>
                                                    <Typography variant="body2" style={{ color: textColor }} id={styles["subtitle"]}>
                                                        {`${stock.exchangesymbol} • ${stock.ticker} `} • {stock.isin}
                                                    </Typography>
                                                    {/* Mobile anchor: badge lives on its own line under the
                                                        subtitle so it doesn't crowd the company-name H1.
                                                        Desktop badge lives further right alongside
                                                        <StockHeaderInfo/>. */}
                                                    {isMobile && <StockReportBadge stock={stock} compact />}
                                                    {isMobile && <TradingIdeaBadge stock={stock} compact />}
                                                    {isMobile && <NextCorporateActionChip stock={stock} compact />}
                                                </Box>
                                                {!isMobile && <StockHeaderInfo stock={stock} />}
                                                {/* Informational sibling of the price / session cluster —
                                                    the upcoming ex-dividend or split from the stock base
                                                    payload. Renders nothing when there is none. */}
                                                {!isMobile && <NextCorporateActionChip stock={stock} />}
                                                {/* Desktop anchor: a growing flex spacer pushes the
                                                    badge to the far right of the header row,
                                                    separating it from the price / session cluster
                                                    so it reads as a standalone call-to-action.
                                                    Uses flexGrow + justifyContent: flex-end (rather
                                                    than ml: auto) so the behaviour holds even when
                                                    the row wraps. */}
                                                {!isMobile && (
                                                    <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}>
                                                        <StockReportBadge stock={stock} />
                                                        <TradingIdeaBadge stock={stock} />
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box sx={{ height: "48px" }}>
                                                <StockLinks stock={stock} />
                                            </Box>
                                        </Box>
                                    }
                                    stockPage
                                    // The peers tab grows with its list (every row and column visible) and scrolls as a page.
                                    autoScroll={page === "peers"}
                                >
                                    <div className={classNames(styles[page], styles["stock-layout"])}>
                                        <div className={styles.content}>
                                            <Container className={styles.muiContainer} maxWidth={false}>
                                                {children}
                                            </Container>
                                        </div>
                                    </div>
                                </ContentLayout>
                            ) : (
                                <Container className={styles.muiContainer} maxWidth={false}>
                                    {children}
                                </Container>
                            )}
                        </ErrorBoundary>
                    }
                </main>

                {/* Data pages only (src/lib/lockedPages.ts). Both sit after <main>, which is
                    a fixed viewport-height box, so neither can squeeze a full-height grid:
                    the explainer is reached by scrolling the root, the panel is fixed. */}
                <AboutThisData stock={stock} />
                <LockedValuePanel stock={stock} />
                {/* Same placement for the overview's key-data sentence and the site
                    footer: server-rendered text and links, below the fixed-height page. */}
                {stock && ["/s/[exchange]/[ticker]", "/s/[exchange]/[ticker]/short-interest"].includes(pathname) && <StockKeyData stock={stock} shortData={shortData} />}
                <SiteFooter stock={stock} />

                <Drawer
                    anchor={anchorPosition}
                    id={id}
                    open={settingsModal}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            paddingTop: "env(safe-area-inset-top)",
                            backgroundColor: "transparent",
                            display: "flex",
                            alignItems: "normal",
                            justifyContent: "flex-start",

                            "& .MuiSelect-icon": {
                                color: theme === "dark" && textColor,
                            },
                        },
                    }}
                >
                    <Card className={styles.drawerContent}>
                        <Box>
                            <Typography variant="h2" sx={{ mb: 2 }}>
                                Tickertape Settings
                            </Typography>
                            <TickertapeSettings isModal />
                        </Box>
                        {user.subscription_plan !== "ORTEX Visitor" ? (
                            <Box sx={{ alignSelf: "center", marginTop: "auto", display: "flex", justifyContent: "center" }}>
                                <Button onClick={goToSettings} variant="text">
                                    All settings
                                </Button>
                            </Box>
                        ) : null}
                    </Card>
                </Drawer>
            </div>
        </div>
    );
};

export default RootLayout;
